// @flow

import axios from 'axios';

import logger from '../../app/logger';

declare var config: Object;

/**
 * Live state of a conference, readable before the client has joined it.
 *
 * Served by Prosody's mod_muc_size out of the in-memory MUC, so it reflects the
 * room as it is right now. The backend's `meeting/totalparticipants` reports a
 * replicated copy of the same numbers that trails the room by however long the
 * write and the read take, which is why guests waiting on it were released
 * seconds after the host had actually arrived.
 */
export type ConferenceInfoParticipant = {

    /** Stable occupant id within the room. */
    id: string,

    /** Display name published by the participant. */
    name: string,

    /** Current MUC role. */
    role: string
};

export type ConferenceInfo = {

    /**
     * False when the room has not been created yet, i.e. nobody has joined. This
     * is a normal pre-join state, not an error.
     */
    exists: boolean,

    /**
     * Occupants a user would see. Excludes focus, recorders and transcribers.
     */
    participants: number,

    /**
     * How many of those occupants hold the moderator role.
     */
    moderators: number,

    /**
     * Whether a host has arrived. Prefer this over comparing participants to
     * zero: a room can hold guests without the meeting having started.
     */
    hasHost: boolean,

    /**
     * Whether a lobby is currently holding people out of the room.
     */
    lobbyEnabled: boolean,

    /**
     * Whether the room only admits members (the lobby's underlying mechanism).
     */
    membersOnly: boolean,

    /**
     * How many guests are knocking on the room's lobby. Always available, like
     * the other counts.
     */
    waiting?: number,

    /**
     * Whether a guest joining now would be routed to a visitor node -- i.e.
     * visitor mode is on for this room, armed by a moderator in the meeting or
     * pre-armed from their pre-join screen before the room existed. Undefined
     * when the deployment does not run visitor mode at all.
     */
    visitorsEnabled?: boolean,

    /**
     * Current occupants, present only when the server verified a moderator JWT.
     * Its absence is an authorization boundary, not an empty room.
     */
    participantList?: Array<ConferenceInfoParticipant>,

    /**
     * Guests waiting in the lobby, under the same authorization rule as
     * {@code participantList}. Kept apart from it because these people are not
     * in the room and must never be shown as if they were.
     */
    waitingList?: Array<ConferenceInfoParticipant>
};

/**
 * How long to wait for Prosody before giving up on a single poll. Short on
 * purpose: this runs on an interval, so a slow answer is better dropped than
 * queued behind the next one.
 */
const REQUEST_TIMEOUT = 5000;

/**
 * In-flight requests keyed by URL/parameters, preventing redundant concurrent
 * HTTP calls to /conference-info for the same room.
 */
const inFlightRequests: Map<string, Promise<?ConferenceInfo>> = new Map();

/**
 * Derives the tenant subdomain, if any, from the configured MUC host.
 *
 * `hosts.muc` is `<prefix>.<domain>` on a single-tenant deployment and
 * `<prefix>.<tenant>.<domain>` when the meeting lives under a tenant. Prosody
 * needs the tenant separately because it addresses such rooms as
 * `[tenant]room@<prefix>.<domain>`.
 *
 * @param {Object} hosts - The `hosts` section of the config.
 * @returns {string} The tenant, or an empty string when there is none.
 */
function getTenantSubdomain(hosts: Object = {}): string {
    const { domain, muc } = hosts;

    if (!domain || !muc || !muc.endsWith(`.${domain}`)) {
        return '';
    }

    // Everything ahead of the base domain: `<prefix>` or `<prefix>.<tenant>`.
    const head = muc.slice(0, muc.length - domain.length - 1);
    const separator = head.indexOf('.');

    return separator === -1 ? '' : head.slice(separator + 1);
}

/**
 * Resolves the origin the Prosody HTTP endpoints are served from.
 *
 * On web they are proxied by the same nginx that served the app, so the app's
 * own origin is correct and keeps the request same-origin. On native there is no
 * document to inherit from and the deployment domain has to be used.
 *
 * @param {string} origin - Explicit origin supplied by the caller.
 * @param {Object} hosts - The `hosts` section of the config.
 * @returns {?string}
 */
function resolveOrigin(origin: ?string, hosts: Object = {}): ?string {
    if (origin) {
        return origin.replace(/\/+$/, '');
    }

    if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin;
    }

    return hosts.domain ? `https://${hosts.domain}` : undefined;
}

/**
 * Reads a value that may legitimately be absent from the response.
 *
 * @param {Object} data - The parsed response body.
 * @param {string} key - The property to read.
 * @param {number} fallback - Value to use when the property is missing.
 * @returns {number}
 */
function readCount(data: Object, key: string, fallback: number = 0): number {
    const value = Number(data[key]);

    return Number.isFinite(value) ? value : fallback;
}

/**
 * Normalizes the moderator-only participant list returned by Prosody.
 *
 * @param {*} value - Candidate API value.
 * @returns {Array<ConferenceInfoParticipant>|void}
 */
function readParticipantList(value: any): ?Array<ConferenceInfoParticipant> {
    if (!Array.isArray(value)) {
        return undefined;
    }

    return value
        .filter(participant => participant && typeof participant === 'object')
        .map(participant => {
            return {
                id: String(participant.id ?? ''),
                name: String(participant.name ?? ''),
                role: String(participant.role ?? 'participant')
            };
        })
        .filter(participant => participant.id);
}

/**
 * Fetches the live state of a conference from Prosody.
 *
 * Returns null whenever the answer cannot be trusted - the endpoint is disabled,
 * the network failed, or something other than the expected JSON came back.
 * Callers must treat null as "unknown" and fall back rather than as "empty
 * room"; the two are not the same and conflating them is what silently blocked
 * the join button when a request failed.
 *
 * @param {Object} options - Request options.
 * @param {string} options.roomName - Backend-safe room name.
 * @param {Object} options.hosts - The `hosts` section of the config. Defaults to
 * the global config on web.
 * @param {string} options.origin - Origin serving the Prosody endpoints.
 * Defaults to the app's own origin on web.
 * @param {string} options.token - JWT. Counts remain available when optional,
 * but participant identities require a verified moderator token.
 * @param {Object} options.signal - AbortSignal to cancel the request.
 * @returns {Promise<?ConferenceInfo>}
 */
export async function fetchConferenceInfo({
    roomName,
    hosts = typeof config === 'undefined' ? undefined : config?.hosts,
    origin,
    token,
    signal
}: Object = {}): Promise<?ConferenceInfo> {
    const resolvedHosts = hosts ?? {};
    const baseUrl = resolveOrigin(origin, resolvedHosts);

    if (!roomName || !baseUrl || !resolvedHosts.domain) {
        return null;
    }

    const dedupeKey = signal ? null : `${baseUrl}:${roomName}:${resolvedHosts.domain}:${token ?? ''}`;

    if (dedupeKey && inFlightRequests.has(dedupeKey)) {
        return inFlightRequests.get(dedupeKey);
    }

    const requestPromise = (async () => {
        const params: Object = {
            room: roomName,
            domain: resolvedHosts.domain
        };
        const subdomain = getTenantSubdomain(resolvedHosts);

        if (subdomain) {
            params.subdomain = subdomain;
        }

        if (token) {
            params.token = token;
        }

        try {
            const { data } = await axios({
                url: `${baseUrl}/conference-info`,
                method: 'get',
                timeout: REQUEST_TIMEOUT,
                params,
                signal
            });

            // When the endpoint is not enabled nginx has no route for this path, so
            // the request falls through to the SPA catch-all and returns index.html
            // with a 200. Shape-check rather than status-check to catch that.
            if (!data || typeof data !== 'object' || typeof data.exists !== 'boolean') {
                return null;
            }

            return {
                exists: data.exists,
                participants: readCount(data, 'participants'),
                moderators: readCount(data, 'moderators'),
                hasHost: Boolean(data.hasHost),
                lobbyEnabled: Boolean(data.lobbyEnabled),
                membersOnly: Boolean(data.membersOnly),
                visitorsEnabled: typeof data.visitorsEnabled === 'boolean' ? data.visitorsEnabled : undefined,
                waiting: readCount(data, 'waiting'),
                participantList: readParticipantList(data.participantList),
                waitingList: readParticipantList(data.waitingList)
            };
        } catch (e) {
            // Expected whenever the deployment has not enabled the endpoint, so this
            // stays at debug: callers fall back on their own.
            logger.debug(`conference-info unavailable for ${roomName}: ${e.message}`);

            return null;
        } finally {
            if (dedupeKey) {
                inFlightRequests.delete(dedupeKey);
            }
        }
    })();

    if (dedupeKey) {
        inFlightRequests.set(dedupeKey, requestPromise);
    }

    return await requestPromise;
}

/**
 * Whether a conference has started, i.e. whether joining it now puts the user
 * into a live meeting rather than an empty room.
 *
 * A host having arrived is the real signal. The occupant count is kept as a
 * second condition because a deployment that does not hand out the moderator
 * role would otherwise never report a started meeting.
 *
 * @param {?ConferenceInfo} info - Conference state, or null when unknown.
 * @returns {boolean} False when the state is unknown.
 */
export function hasConferenceStarted(info: ?ConferenceInfo): boolean {
    if (!info) {
        return false;
    }

    return info.hasHost || info.participants >= 1;
}
